import { useEffect, useState } from 'react';

import {
  Box,
  Chip,
  Divider,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { UnitApi } from '@/api';
import { IcClock } from '@/assets/images';
import { ButtonWithDoubleClickPrevention } from '@/components';
import { DATE_TIME_FORMAT, TIME_FORMAT } from '@/config';
import { IUnit } from '@/models';
import { RadioClickData } from '@/models/radio.click.data';
import { getAccountHeadquarters } from '@/services';
import { useUserStore } from '@/store';

interface RadioClicksPopoverProps {
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  handleClose: () => void;
  selectedRadioClick: RadioClickData | null;
  handleAssignUnitShift: (
    unitShiftId: string,
    unit?: IUnit,
    isDispatchedToHQ?: boolean,
    headquarterName?: string | null,
  ) => void;
  cfs: any;
}

export const RadioClicksPopover = ({
  open,
  anchorEl,
  handleClose,
  selectedRadioClick,
  handleAssignUnitShift,
}: RadioClicksPopoverProps) => {
  const theme = useTheme();
  const { account } = useUserStore();
  const headquarters = getAccountHeadquarters(account);
  const [selectedUnit, setSelectedUnit] = useState<IUnit | undefined>(
    undefined,
  );
  const [loadingUnit, setLoadingUnit] = useState(false);

  if (!selectedRadioClick) return null;

  if (!selectedRadioClick.unitId) {
    return null;
  }

  useEffect(() => {
    if (selectedRadioClick.unitId && !selectedRadioClick.unitShiftId) {
      setLoadingUnit(true);
      UnitApi.getUnit(selectedRadioClick.unitId).then((res) => {
        setSelectedUnit(res.data);
        setLoadingUnit(false);
      });
    }
  }, [selectedRadioClick.unitId]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Stack minWidth={270}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ p: 2 }}
          justifyContent="space-between"
        >
          <Typography>
            {selectedRadioClick.equipment || selectedRadioClick.radioId}
          </Typography>
          <Tooltip
            title={moment(selectedRadioClick.radioClickTime).format(
              DATE_TIME_FORMAT,
            )}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IcClock height="14px" color={theme.palette.error.main} />
              <Typography>
                {moment(selectedRadioClick.radioClickTime).format(TIME_FORMAT)}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>
        <Divider sx={{ mx: 2 }} />
        <Stack
          direction="row"
          spacing={2}
          sx={{ p: 2 }}
          justifyContent="space-between"
        >
          {selectedRadioClick.cfs ? (
            <>
              <Typography
                color={theme.palette.text.primary}
                fontWeight={500}
                fontSize={15}
              >
                {selectedRadioClick.cfs}
              </Typography>
              {!selectedRadioClick.isDispatchedFromHQ && (
                <Chip color="primary" label="Direct" size="small" />
              )}
            </>
          ) : (
            <ButtonWithDoubleClickPrevention
              color="error"
              variant="contained"
              size="large"
              onClick={() =>
                handleAssignUnitShift(
                  selectedRadioClick.unitShiftId as string,
                  selectedUnit,
                )
              }
              isDisabled={loadingUnit}
            >
              Assign
            </ButtonWithDoubleClickPrevention>
          )}

          {selectedRadioClick.isDispatchedFromHQ && (
            <>
              {selectedRadioClick.cfs ? (
                <Chip
                  color="primary"
                  label={selectedRadioClick.headquarterName || 'To HQ'}
                  size="small"
                />
              ) : (
                <>
                  {headquarters.map((headquarter) => (
                    <ButtonWithDoubleClickPrevention
                      key={headquarter.name}
                      variant="outlined"
                      color="inherit"
                      size="large"
                      onClick={() =>
                        handleAssignUnitShift(
                          selectedRadioClick.unitShiftId as string,
                          selectedUnit,
                          true,
                          headquarter.name,
                        )
                      }
                      isDisabled={loadingUnit}
                      sx={{ mr: 2, padding: '8px 20px' }}
                    >
                      Assign to {headquarter.name}
                    </ButtonWithDoubleClickPrevention>
                  ))}
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Popover>
  );
};
